<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { RouterLink } from 'vue-router'
import { gamesApi } from '@/api/games'
import type { GameScheduleEntry, GamesScheduleResponse } from '@/api/games'
import { adminApi } from '@/api/admin'
import { useAuth } from '@/composables/useAuth'
import { formatOdds, formatPct, formatEdge, edgeClass } from '@/utils/odds'

const loading = ref(false)
const error = ref<string | null>(null)
const fromDate = ref<string>('')
const toDate = ref<string>('')
const dateFrom = ref('')
const dateTo = ref('')
const games = ref<GameScheduleEntry[]>([])
const { user } = useAuth()
const refreshingGamePk = ref<number | null>(null)
const simulatingGameId = ref<number | null>(null)
const simulatedConfirmId = ref<number | null>(null)
const pullingPropsGameId = ref<number | null>(null)
const propsPulledConfirmId = ref<number | null>(null)

function ordinal(n: number): string {
  const mod100 = n % 100
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`
  switch (n % 10) {
    case 1:
      return `${n}st`
    case 2:
      return `${n}nd`
    case 3:
      return `${n}rd`
    default:
      return `${n}th`
  }
}

type SubGroup = { label: string; games: GameScheduleEntry[] }
type DayGroup = { label: string; subGroups: SubGroup[] }

const STATUS_BUCKET_ORDER = ['in_progress', 'delayed', 'scheduled', 'postponed', 'final'] as const
const STATUS_BUCKET_LABEL: Record<string, string> = {
  in_progress: 'Live',
  delayed: 'Delayed',
  scheduled: 'Later',
  postponed: 'PPD',
  final: 'Final'
}

const groupedGames = computed<DayGroup[]>(() => {
  // Collect unique dates from games, sorted ascending
  const allDates = [...new Set(games.value.map(g => g.game_date))].sort()
  const defaultFrom = fromDate.value
  const defaultTo = toDate.value

  return allDates
    .map(day => {
      const dayGames = games.value.filter(g => g.game_date === day)
      const subGroups: SubGroup[] = STATUS_BUCKET_ORDER.map(status => ({
        label: STATUS_BUCKET_LABEL[status],
        games: dayGames.filter(g => g.status === status)
      })).filter(sg => sg.games.length > 0)

      // Use friendly labels only for the default two-day window
      let label = day
      if (!dateFrom.value && !dateTo.value) {
        if (day === defaultFrom) label = 'Today'
        else if (day === defaultTo) label = 'Tomorrow'
      }

      return { label, subGroups }
    })
    .filter(dg => dg.subGroups.length > 0)
})

async function loadGames(): Promise<void> {
  loading.value = true
  error.value = null
  try {
    const filters: Record<string, string | number> = {}
    if (dateFrom.value) filters.date_from = dateFrom.value
    if (dateTo.value) filters.date_to = dateTo.value

    const data: GamesScheduleResponse = await gamesApi.getSchedule(filters)
    games.value = data.games
    fromDate.value = data.from_date
    toDate.value = data.to_date
  } catch (err: unknown) {
    const apiErr = err as { detail?: string }
    error.value = apiErr.detail || 'Failed to load upcoming games'
    games.value = []
  } finally {
    loading.value = false
  }
}

function clearFilters(): void {
  dateFrom.value = ''
  dateTo.value = ''

  loadGames()
}

async function refreshGame(gamePk: number): Promise<void> {
  refreshingGamePk.value = gamePk
  try {
    const { job_id } = await adminApi.triggerRefreshGame(gamePk)

    // Poll job status until complete/failed (up to 60s), then refresh game list
    const deadline = Date.now() + 60_000
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 2000))
      const jobStatus = await adminApi.getJobStatus(job_id)
      if (
        jobStatus.status === 'complete' ||
        jobStatus.status === 'failed' ||
        jobStatus.status === 'aborted'
      ) {
        break
      }
    }
    // Refresh game list regardless of outcome so UI reflects latest DB state
    await loadGames()
  } finally {
    refreshingGamePk.value = null
  }
}

async function simulateGame(gameId: number): Promise<void> {
  simulatingGameId.value = gameId
  error.value = null
  try {
    const { job_id } = await adminApi.triggerSimulateGame(gameId)

    // Poll until complete or failed (up to 3 minutes)
    const deadline = Date.now() + 180_000
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 3000))
      const status = await adminApi.getJobStatus(job_id)
      if (status.status === 'complete') {
        simulatedConfirmId.value = gameId
        // Refresh the schedule so sim win% updates on the card
        await loadGames()
        await new Promise(r => setTimeout(r, 2000))
        simulatedConfirmId.value = null
        return
      }
      if (status.status === 'failed' || status.status === 'aborted') {
        error.value = `Simulation failed: ${status.result?.reason ?? 'unknown error'}`
        return
      }
    }
    error.value = 'Simulation timed out — check queue status'
  } catch (err: unknown) {
    const apiErr = err as { response?: { data?: { detail?: string } } }
    error.value = apiErr.response?.data?.detail ?? 'Failed to enqueue simulation'
  } finally {
    simulatingGameId.value = null
  }
}

async function pullProps(gameId: number): Promise<void> {
  pullingPropsGameId.value = gameId
  error.value = null
  try {
    const { job_id } = await adminApi.triggerPullOdds(true, gameId)

    // Poll until complete or failed (up to 90s)
    const deadline = Date.now() + 90_000
    while (Date.now() < deadline) {
      await new Promise(r => setTimeout(r, 3000))
      const status = await adminApi.getJobStatus(job_id)
      if (status.status === 'complete') {
        // A completed job can still write 0 props if the book isn't open for
        // this game yet (or its event didn't match) — surface that instead of
        // a misleading "Pulled ✓".
        const created = Number(status.result?.prop_lines_created ?? 0)
        if (created > 0) {
          propsPulledConfirmId.value = gameId
          await new Promise(r => setTimeout(r, 2000))
          propsPulledConfirmId.value = null
        } else {
          error.value = 'No prop lines available for this game yet'
        }
        return
      }
      if (status.status === 'failed' || status.status === 'aborted') {
        error.value = 'Prop pull failed — check queue status'
        return
      }
    }
    error.value = 'Prop pull timed out — check queue status'
  } catch (err: unknown) {
    const apiErr = err as { response?: { data?: { detail?: string } } }
    error.value = apiErr.response?.data?.detail ?? 'Failed to enqueue prop pull'
  } finally {
    pullingPropsGameId.value = null
  }
}

onMounted(loadGames)
</script>

<template>
  <div class="tg-panel">
    <!-- Contents-style header -->
    <div class="tg-panel__header">
      <div class="tg-panel__header-left">
        <p class="tg-panel__eyebrow">Contents &mdash; The Slate</p>
        <h2 class="tg-panel__heading">
          Upcoming Games
          <span v-if="fromDate" class="tg-panel__date"
            >{{ fromDate
            }}<template v-if="toDate && toDate !== fromDate"> &ndash; {{ toDate }}</template></span
          >
        </h2>
      </div>
      <button
        class="btn-press btn-press--ghost btn-press--sm"
        :disabled="loading"
        @click="loadGames"
      >
        {{ loading ? 'Loading…' : 'Refresh' }}
      </button>
    </div>

    <hr class="tg-panel__rule" />

    <!-- Filters -->
    <div class="schedule-filters">
      <div class="schedule-filters__field">
        <label for="tg-from">From</label>
        <input id="tg-from" v-model="dateFrom" type="date" @change="loadGames" />
      </div>
      <div class="schedule-filters__field">
        <label for="tg-to">To</label>
        <input id="tg-to" v-model="dateTo" type="date" @change="loadGames" />
      </div>
      <button class="schedule-filters__clear" @click="clearFilters">Clear</button>
    </div>

    <div v-if="loading && games.length === 0" class="tg-panel__loading">
      <span class="tg-panel__loading-mark">♦</span> Filing the slate…
    </div>

    <div v-else-if="error" class="tg-panel__error" role="alert">
      <span class="tg-panel__error-mark">!</span>
      <span>{{ error }}</span>
    </div>

    <p v-else-if="!loading && games.length === 0" class="tg-panel__empty">
      <em>No upcoming games</em> on the schedule &mdash; check back tomorrow.
    </p>

    <div v-else class="tg-groups">
      <section v-for="dayGroup in groupedGames" :key="dayGroup.label" class="tg-day">
        <h3 class="tg-day__label">
          <span class="tg-day__marker" aria-hidden="true">§</span>
          {{ dayGroup.label }}
        </h3>

        <div v-for="subGroup in dayGroup.subGroups" :key="subGroup.label" class="tg-subgroup">
          <div class="tg-subgroup__header">
            <span
              class="tg-subgroup__label"
              :class="`tg-subgroup__label--${subGroup.label.toLowerCase()}`"
              >{{ subGroup.label }}</span
            >
            <span class="tg-subgroup__rule" aria-hidden="true"></span>
            <span class="tg-subgroup__count">№ {{ subGroup.games.length }}</span>
          </div>

          <div class="tg-grid">
            <article
              v-for="game in subGroup.games"
              :key="game.game_id"
              class="game-card paper-card"
            >
              <header class="game-card__header">
                <div class="game-card__header-left">
                  <span class="game-card__matchup"
                    ><em class="game-card__away">{{ game.away_abbreviation }}</em>
                    <span class="game-card__at">@</span>
                    <span class="game-card__home">{{ game.home_abbreviation }}</span></span
                  >
                  <span v-if="game.game_time" class="game-card__gametime numeric">{{
                    game.game_time
                  }}</span>
                </div>
                <div class="game-card__header-right">
                  <RouterLink
                    v-if="game.home_sim_win_pct != null"
                    :to="`/simulate/${game.game_id}`"
                    class="game-card__sim-link"
                  >
                    View Simulation <span aria-hidden="true">&rarr;</span>
                  </RouterLink>
                  <div v-if="user?.is_superuser" class="game-card__admin-buttons">
                    <button
                      class="btn btn--small btn--refresh"
                      :disabled="refreshingGamePk === game.game_id"
                      @click="refreshGame(game.game_id)"
                    >
                      {{ refreshingGamePk === game.game_id ? '…' : 'Sync' }}
                    </button>
                    <button
                      class="btn btn--small btn--refresh"
                      :class="{ 'btn--queued': simulatedConfirmId === game.game_id }"
                      :disabled="
                        simulatingGameId === game.game_id || simulatedConfirmId === game.game_id
                      "
                      @click="simulateGame(game.game_id)"
                    >
                      {{
                        simulatingGameId === game.game_id
                          ? '⟳'
                          : simulatedConfirmId === game.game_id
                            ? 'Queued ✓'
                            : 'Re-sim'
                      }}
                    </button>
                    <button
                      class="btn btn--small btn--refresh"
                      :class="{ 'btn--queued': propsPulledConfirmId === game.game_id }"
                      :disabled="
                        pullingPropsGameId === game.game_id || propsPulledConfirmId === game.game_id
                      "
                      title="Pull player prop lines for this game"
                      @click="pullProps(game.game_id)"
                    >
                      {{
                        pullingPropsGameId === game.game_id
                          ? '⟳'
                          : propsPulledConfirmId === game.game_id
                            ? 'Pulled ✓'
                            : 'Pull Props'
                      }}
                    </button>
                  </div>
                </div>
              </header>

              <!-- Matchup (dotted-leader like boxscore) -->
              <div class="game-card__teams">
                <div
                  class="game-card__team-row"
                  :class="{
                    'game-card__team-row--winner':
                      game.status === 'final' &&
                      game.away_score != null &&
                      game.home_score != null &&
                      game.away_score > game.home_score
                  }"
                >
                  <span class="game-card__team-tag">AWAY</span>
                  <span class="game-card__team-name">{{ game.away_team }}</span>
                  <span class="game-card__team-dots" aria-hidden="true"></span>
                  <span
                    v-if="game.status === 'final' || game.status === 'in_progress'"
                    class="game-card__team-score numeric"
                    >{{ game.away_score ?? '—' }}</span
                  >
                </div>
                <div
                  class="game-card__team-row"
                  :class="{
                    'game-card__team-row--winner':
                      game.status === 'final' &&
                      game.away_score != null &&
                      game.home_score != null &&
                      game.home_score > game.away_score
                  }"
                >
                  <span class="game-card__team-tag">HOME</span>
                  <span class="game-card__team-name">{{ game.home_team }}</span>
                  <span class="game-card__team-dots" aria-hidden="true"></span>
                  <span
                    v-if="game.status === 'final' || game.status === 'in_progress'"
                    class="game-card__team-score numeric"
                    >{{ game.home_score ?? '—' }}</span
                  >
                </div>
              </div>

              <div
                v-if="game.away_probable_pitcher || game.home_probable_pitcher"
                class="game-card__pitchers"
              >
                <span class="game-card__pitcher-label">Probable</span>
                <span class="game-card__pitcher">{{ game.away_probable_pitcher ?? 'TBD' }}</span>
                <span class="game-card__pitcher-sep" aria-hidden="true">/</span>
                <span class="game-card__pitcher">{{ game.home_probable_pitcher ?? 'TBD' }}</span>
              </div>

              <div
                v-if="game.status === 'in_progress' && game.inning"
                class="game-card__inning-line"
              >
                <span class="game-card__inning-label">Inning</span>
                <span class="game-card__inning numeric">{{ ordinal(game.inning) }}</span>
              </div>

              <div
                v-if="
                  game.status === 'scheduled' ||
                  game.status === 'delayed' ||
                  game.status === 'in_progress'
                "
                class="game-card__badges"
              >
                <span
                  class="badge"
                  :class="
                    game.away_lineup_confirmed && game.home_lineup_confirmed
                      ? 'badge--confirmed'
                      : 'badge--pending'
                  "
                >
                  {{
                    game.away_lineup_confirmed && game.home_lineup_confirmed
                      ? 'Lineups In'
                      : 'Lineup Pending'
                  }}
                </span>
                <span v-if="game.home_sim_win_pct != null" class="badge badge--sim">Sim Ready</span>
              </div>

              <div v-if="game.status !== 'final'" class="game-card__odds">
                <div class="odds-col">
                  <span class="odds-col__label">{{ game.away_abbreviation }}</span>
                  <span class="odds-col__price numeric">{{
                    formatOdds(game.away_odds.best_price)
                  }}</span>
                  <span v-if="game.away_odds.best_book" class="odds-col__book">{{
                    game.away_odds.best_book
                  }}</span>
                  <span class="odds-col__implied numeric">{{
                    formatPct(game.away_odds.implied_pct)
                  }}</span>
                </div>
                <div class="odds-col">
                  <span class="odds-col__label">{{ game.home_abbreviation }}</span>
                  <span class="odds-col__price numeric">{{
                    formatOdds(game.home_odds.best_price)
                  }}</span>
                  <span v-if="game.home_odds.best_book" class="odds-col__book">{{
                    game.home_odds.best_book
                  }}</span>
                  <span class="odds-col__implied numeric">{{
                    formatPct(game.home_odds.implied_pct)
                  }}</span>
                </div>
              </div>

              <div v-if="game.status !== 'final' && game.best_total" class="game-card__totals">
                <span class="totals-row__label">O/U {{ game.best_total.line }}</span>
                <div class="totals-row">
                  <span class="totals-row__side numeric">
                    Over {{ formatOdds(game.best_total.over_price) }}
                  </span>
                  <span class="totals-row__sep" aria-hidden="true">/</span>
                  <span class="totals-row__side numeric">
                    Under {{ formatOdds(game.best_total.under_price) }}
                  </span>
                  <span v-if="game.best_total.best_book" class="totals-row__book">
                    {{ game.best_total.best_book }}
                  </span>
                </div>
                <div
                  v-if="
                    (game.status === 'scheduled' || game.status === 'delayed') &&
                    game.sim_over_pct != null
                  "
                  class="totals-row__sim"
                >
                  <span class="totals-row__sim-label">Sim</span>
                  <span :class="game.sim_over_pct > 0.5 ? 'totals-sim--over' : 'totals-sim--under'">
                    {{ game.sim_over_pct > 0.5 ? 'Over' : 'Under' }}
                    <span class="numeric">{{
                      formatPct(game.sim_over_pct > 0.5 ? game.sim_over_pct : 1 - game.sim_over_pct)
                    }}</span>
                  </span>
                </div>
              </div>

              <div
                v-if="
                  (game.status === 'scheduled' || game.status === 'delayed') &&
                  (game.home_sim_win_pct !== null || game.away_sim_win_pct !== null)
                "
                class="game-card__sim"
              >
                <span class="sim-row__label">Sim Win%</span>
                <div class="sim-row">
                  <span
                    class="sim-row__team"
                    :class="{
                      'sim-row__team--leader':
                        (game.away_sim_win_pct ?? 0) > (game.home_sim_win_pct ?? 0)
                    }"
                    >{{ game.away_abbreviation }}</span
                  >
                  <span
                    class="sim-row__pct numeric"
                    :class="{
                      'sim-row__pct--leader':
                        (game.away_sim_win_pct ?? 0) > (game.home_sim_win_pct ?? 0)
                    }"
                    >{{ formatPct(game.away_sim_win_pct) }}</span
                  >
                  <span class="sim-row__sep" aria-hidden="true">·</span>
                  <span
                    class="sim-row__pct numeric"
                    :class="{
                      'sim-row__pct--leader':
                        (game.home_sim_win_pct ?? 0) > (game.away_sim_win_pct ?? 0)
                    }"
                    >{{ formatPct(game.home_sim_win_pct) }}</span
                  >
                  <span
                    class="sim-row__team"
                    :class="{
                      'sim-row__team--leader':
                        (game.home_sim_win_pct ?? 0) > (game.away_sim_win_pct ?? 0)
                    }"
                    >{{ game.home_abbreviation }}</span
                  >
                </div>
              </div>

              <div
                v-if="
                  (game.status === 'scheduled' || game.status === 'delayed') &&
                  (game.home_edge !== null || game.away_edge !== null || game.ou_edge !== null)
                "
                class="game-card__edges"
              >
                <div v-if="game.away_edge !== null && game.away_edge > 0" class="edge-item">
                  <span class="edge-item__label">{{ game.away_abbreviation }} Edge</span>
                  <span class="edge-item__value edge--positive numeric">{{
                    formatEdge(game.away_edge)
                  }}</span>
                </div>
                <div v-if="game.home_edge !== null && game.home_edge > 0" class="edge-item">
                  <span class="edge-item__label">{{ game.home_abbreviation }} Edge</span>
                  <span class="edge-item__value edge--positive numeric">{{
                    formatEdge(game.home_edge)
                  }}</span>
                </div>
                <!-- O/U edge intentionally shows both positive and negative values:
                     the label dynamically reflects the sim-favored direction
                     (Over/Under), unlike ML edges which only show the positive side. -->
                <div v-if="game.ou_edge !== null" class="edge-item">
                  <span class="edge-item__label"
                    >{{ game.ou_edge >= 0 ? 'Over' : 'Under' }} Edge</span
                  >
                  <span
                    class="edge-item__value numeric"
                    :class="edgeClass(Math.abs(game.ou_edge))"
                    >{{ formatEdge(Math.abs(game.ou_edge)) }}</span
                  >
                </div>
              </div>
            </article>
          </div>
        </div>
      </section>
    </div>
  </div>
</template>

<style scoped>
/* ============================================================================
   Pressbox-aesthetic rewrite.
   Everything on cream paper. Uses only tokens from pressbox.css.
   ============================================================================ */

.tg-panel {
  color: var(--ink);
}

/* ─── Panel header ───────────────────────────────────────────────────────── */
.tg-panel__header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.75rem;
  flex-wrap: wrap;
}

.tg-panel__header-left {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.tg-panel__eyebrow {
  font-family: var(--font-ui);
  font-size: 0.68rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.22em;
  color: var(--ox);
}

.tg-panel__heading {
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 36,
    'wght' 700;
  font-size: 1.75rem;
  letter-spacing: -0.01em;
  color: var(--ink);
  display: flex;
  align-items: baseline;
  gap: 0.9rem;
  flex-wrap: wrap;
}

.tg-panel__date {
  font-family: var(--font-data);
  font-variant-numeric: tabular-nums;
  font-size: 0.78rem;
  font-weight: 400;
  color: var(--ink-faint);
  letter-spacing: 0;
}

.tg-panel__rule {
  border: none;
  border-top: var(--rule-thin) solid var(--ink);
  border-bottom: var(--rule-thin) solid var(--ink);
  height: 4px;
  margin: 0.25rem 0 1.1rem;
}

/* Compact refresh button variant (keeps btn-press aesthetic but tighter). */
.btn-press--sm {
  padding: 0.55rem 1.1rem;
  font-size: 0.66rem;
  letter-spacing: 0.2em;
}

/* ─── States: loading / error / empty ────────────────────────────────────── */
.tg-panel__loading,
.tg-panel__empty {
  font-family: var(--font-editorial);
  font-size: 1rem;
  color: var(--ink-faint);
  padding: 1.75rem 0;
  font-style: italic;
  text-align: center;
  border-top: var(--rule-thin) dashed var(--paper-edge);
  border-bottom: var(--rule-thin) dashed var(--paper-edge);
}

.tg-panel__empty em {
  font-style: italic;
  color: var(--ox);
}

.tg-panel__loading-mark {
  color: var(--ox);
  margin-right: 0.4rem;
}

.tg-panel__error {
  display: flex;
  align-items: center;
  gap: 0.8rem;
  font-family: var(--font-editorial);
  font-size: 0.95rem;
  color: var(--ox);
  padding: 0.85rem 1.1rem;
  border: var(--rule-thin) solid var(--ox);
  background: var(--ox-10);
}

.tg-panel__error-mark {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 50%;
  background: var(--ox);
  color: var(--paper);
  font-family: var(--font-display);
  font-weight: 700;
  font-size: 0.9rem;
  flex-shrink: 0;
}

/* ─── Day / subgroup groupings ───────────────────────────────────────────── */
.tg-groups {
  display: flex;
  flex-direction: column;
  gap: 2.5rem;
}

.tg-day__label {
  display: flex;
  align-items: baseline;
  gap: 0.65rem;
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 48,
    'wght' 700;
  font-size: 1.85rem;
  font-style: italic;
  letter-spacing: -0.01em;
  color: var(--ink);
  margin: 0 0 1rem;
  padding-bottom: 0.45rem;
  border-bottom: var(--rule-thin) solid var(--ink);
}

.tg-day__marker {
  font-family: var(--font-display);
  font-weight: 600;
  color: var(--ox);
  font-size: 1.4rem;
  font-style: italic;
}

.tg-subgroup {
  margin-bottom: 1.75rem;
}

.tg-subgroup:last-child {
  margin-bottom: 0;
}

.tg-subgroup__header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.9rem;
}

.tg-subgroup__label {
  font-family: var(--font-ui);
  font-size: 0.68rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--ink);
  padding: 0.22rem 0.55rem;
  border: var(--rule-thin) solid var(--ink);
  background: var(--paper);
}

.tg-subgroup__label--live {
  color: var(--paper);
  background: var(--gold);
  border-color: var(--gold);
}

.tg-subgroup__label--delayed {
  color: var(--paper);
  background: var(--ox);
  border-color: var(--ox);
}

.tg-subgroup__label--later {
  color: var(--paper);
  background: var(--ink);
  border-color: var(--ink);
}

.tg-subgroup__label--ppd {
  color: var(--ox);
  background: var(--paper);
  border-color: var(--ox);
}

.tg-subgroup__label--final {
  color: var(--ink-faint);
  background: var(--paper);
  border-color: var(--ink-faint);
}

.tg-subgroup__rule {
  flex: 1;
  height: 1px;
  border-top: var(--rule-thin) dashed var(--ink-20);
}

.tg-subgroup__count {
  font-family: var(--font-data);
  font-size: 0.7rem;
  color: var(--ink-faint);
  letter-spacing: 0.05em;
}

/* ─── Responsive grid ────────────────────────────────────────────────────── */
.tg-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
}

@media (min-width: 640px) {
  .tg-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1024px) {
  .tg-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* ─── Game card (paper-card override) ────────────────────────────────────── */
.game-card {
  background: var(--paper);
  border: var(--rule-thin) solid var(--ink);
  padding: 1.1rem 1.15rem 1.2rem;
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  position: relative;
  box-shadow: 3px 3px 0 var(--ink);
  transition:
    transform 0.16s ease,
    box-shadow 0.16s ease;
}

.game-card:hover {
  transform: translate(-1px, -1px);
  box-shadow: 5px 5px 0 var(--ink);
}

.game-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding-bottom: 0.6rem;
  border-bottom: var(--rule-thin) solid var(--ink);
}

.game-card__header-left {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.game-card__matchup {
  display: inline-flex;
  align-items: baseline;
  gap: 0.45rem;
  font-family: var(--font-display);
  font-variation-settings:
    'opsz' 48,
    'wght' 700;
  font-size: 1.55rem;
  line-height: 1;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.game-card__away {
  font-style: italic;
  color: var(--ox);
}

.game-card__at {
  font-family: var(--font-editorial);
  font-size: 0.95rem;
  color: var(--ink-faint);
  font-weight: 400;
  font-style: italic;
  transform: translateY(-2px);
}

.game-card__home {
  color: var(--ink);
}

.game-card__gametime {
  font-family: var(--font-data);
  font-size: 0.78rem;
  color: var(--ink-medium);
  letter-spacing: 0.03em;
}

.game-card__header-right {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 0.4rem;
  flex-shrink: 0;
}

.game-card__sim-link {
  font-family: var(--font-ui);
  font-size: 0.64rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--ink);
  text-decoration: none;
  border-bottom: var(--rule-thin) solid var(--ink);
  padding-bottom: 1px;
  white-space: nowrap;
  transition:
    color 0.15s ease,
    border-color 0.15s ease;
}

.game-card__sim-link:hover {
  color: var(--ox);
  border-bottom-color: var(--ox);
}

.game-card__admin-buttons {
  display: flex;
  gap: 0.35rem;
}

/* ─── Matchup: dotted-leader team rows (like boxscore) ───────────────────── */
.game-card__teams {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.2rem 0 0.3rem;
}

.game-card__team-row {
  display: grid;
  grid-template-columns: auto 1fr auto auto;
  align-items: baseline;
  gap: 0.5rem;
  padding: 0.3rem 0;
  border-bottom: var(--rule-thin) dashed var(--paper-edge);
  color: var(--ink-medium);
}

.game-card__team-row:last-child {
  border-bottom: none;
}

.game-card__team-row--winner {
  color: var(--ink);
}

.game-card__team-row--winner .game-card__team-name {
  font-weight: 700;
}

.game-card__team-row--winner .game-card__team-score {
  color: var(--ox);
  font-weight: 700;
}

.game-card__team-tag {
  font-family: var(--font-ui);
  font-size: 0.55rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  color: var(--ink-ghost);
  text-transform: uppercase;
}

.game-card__team-name {
  font-family: var(--font-editorial);
  font-size: 0.95rem;
  line-height: 1.25;
  color: inherit;
  min-width: 0;
}

.game-card__team-dots {
  height: 1em;
  border-bottom: 1px dotted var(--ink-ghost);
  margin: 0 0.15rem;
  transform: translateY(-0.3em);
}

.game-card__team-score {
  font-family: var(--font-data);
  font-variant-numeric: tabular-nums;
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--ink);
}

/* ─── Probable pitchers ──────────────────────────────────────────────────── */
.game-card__pitchers {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
  padding-top: 0.2rem;
  flex-wrap: wrap;
}

.game-card__pitcher-label {
  font-family: var(--font-ui);
  font-size: 0.58rem;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--ox);
}

.game-card__pitcher {
  font-family: var(--font-editorial);
  font-style: italic;
  font-size: 0.82rem;
  color: var(--ink-medium);
}

.game-card__pitcher-sep {
  font-family: var(--font-editorial);
  font-size: 0.78rem;
  color: var(--ink-ghost);
}

/* ─── Inning line (live) ─────────────────────────────────────────────────── */
.game-card__inning-line {
  display: flex;
  align-items: baseline;
  gap: 0.55rem;
  border-top: var(--rule-thin) dashed var(--paper-edge);
  padding-top: 0.55rem;
}

.game-card__inning-label {
  font-family: var(--font-ui);
  font-size: 0.58rem;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  font-weight: 600;
  color: var(--ox);
}

.game-card__inning {
  font-family: var(--font-data);
  font-size: 0.95rem;
  font-weight: 700;
  color: var(--ink);
}

/* ─── Status badges ──────────────────────────────────────────────────────── */
.game-card__badges {
  display: flex;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.badge {
  font-family: var(--font-ui);
  font-size: 0.6rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  padding: 0.22rem 0.55rem;
  border: var(--rule-thin) solid currentColor;
  background: var(--paper);
}

.badge--confirmed {
  color: var(--ivy);
}

.badge--pending {
  color: var(--gold);
}

.badge--sim {
  color: var(--ink);
  background: var(--paper-deep);
}

/* ─── Odds ───────────────────────────────────────────────────────────────── */
.game-card__odds {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
  border-top: var(--rule-thin) solid var(--ink);
  padding-top: 0.75rem;
}

.odds-col {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.odds-col__label {
  font-family: var(--font-ui);
  font-size: 0.6rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--ink-faint);
}

.odds-col__price {
  font-family: var(--font-data);
  font-variant-numeric: tabular-nums;
  font-size: 1.3rem;
  font-weight: 700;
  color: var(--ink);
  line-height: 1;
}

.odds-col__book {
  font-family: var(--font-ui);
  font-size: 0.58rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ox);
  font-weight: 600;
}

.odds-col__implied {
  font-family: var(--font-data);
  font-size: 0.72rem;
  color: var(--ink-faint);
}

/* ─── Totals ─────────────────────────────────────────────────────────────── */
.game-card__totals {
  border-top: var(--rule-thin) dashed var(--paper-edge);
  padding-top: 0.65rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.totals-row__label {
  font-family: var(--font-ui);
  font-size: 0.6rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--ox);
}

.totals-row {
  display: flex;
  align-items: baseline;
  gap: 0.45rem;
  font-family: var(--font-data);
  font-size: 0.85rem;
  color: var(--ink);
  flex-wrap: wrap;
}

.totals-row__sep {
  color: var(--ink-ghost);
}

.totals-row__book {
  font-family: var(--font-ui);
  font-size: 0.58rem;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ox);
  margin-left: 0.25rem;
  font-weight: 600;
}

.totals-row__sim {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
  font-family: var(--font-editorial);
  font-size: 0.82rem;
  color: var(--ink-medium);
  font-style: italic;
}

.totals-row__sim-label {
  font-family: var(--font-ui);
  font-size: 0.58rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--ink-faint);
  font-style: normal;
  font-weight: 600;
}

.totals-sim--over {
  color: var(--ivy);
  font-weight: 700;
}

.totals-sim--under {
  color: var(--bruise);
  font-weight: 700;
}

/* ─── Sim win% ───────────────────────────────────────────────────────────── */
.game-card__sim {
  border-top: var(--rule-thin) dashed var(--paper-edge);
  padding-top: 0.65rem;
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
}

.sim-row__label {
  font-family: var(--font-ui);
  font-size: 0.6rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-weight: 700;
  color: var(--ox);
}

.sim-row {
  display: flex;
  align-items: baseline;
  gap: 0.55rem;
  flex-wrap: wrap;
}

.sim-row__team {
  font-family: var(--font-ui);
  font-size: 0.64rem;
  font-weight: 600;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--ink-faint);
}

.sim-row__team--leader {
  color: var(--ink);
  font-weight: 700;
}

.sim-row__pct {
  font-family: var(--font-data);
  font-variant-numeric: tabular-nums;
  font-size: 0.9rem;
  color: var(--ink-faint);
  font-weight: 400;
}

.sim-row__pct--leader {
  color: var(--ivy);
  font-weight: 700;
  font-size: 1.15rem;
}

.sim-row__sep {
  color: var(--ink-ghost);
  font-family: var(--font-display);
}

/* ─── Edges ──────────────────────────────────────────────────────────────── */
.game-card__edges {
  border-top: var(--rule-thin) solid var(--ink);
  padding-top: 0.7rem;
  display: flex;
  gap: 1.25rem;
  flex-wrap: wrap;
}

.edge-item {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
}

.edge-item__label {
  font-family: var(--font-ui);
  font-size: 0.58rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  font-weight: 600;
  color: var(--ink-faint);
}

.edge-item__value {
  font-family: var(--font-data);
  font-variant-numeric: tabular-nums;
  font-size: 1rem;
  font-weight: 700;
}

.edge--positive {
  color: var(--ivy);
}

.edge--negative {
  color: var(--bruise);
}

/* ─── Admin buttons (ghost-style) ────────────────────────────────────────── */
.btn {
  font-family: var(--font-ui);
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  padding: 0.35rem 0.75rem;
  border: var(--rule-thin) solid var(--ink);
  background: var(--paper);
  color: var(--ink);
  cursor: pointer;
  transition:
    background 0.15s ease,
    color 0.15s ease,
    border-color 0.15s ease;
  white-space: nowrap;
}

.btn:hover:not(:disabled) {
  background: var(--ink);
  color: var(--paper);
}

.btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn--small {
  font-size: 0.58rem;
  padding: 0.28rem 0.6rem;
}

.btn--refresh {
  color: var(--ink-medium);
  border-color: var(--ink-medium);
}

.btn--refresh:hover:not(:disabled) {
  background: var(--ox);
  color: var(--paper);
  border-color: var(--ox);
}

.btn--queued {
  color: var(--paper) !important;
  background: var(--ivy) !important;
  border-color: var(--ivy) !important;
}

/* ─── Schedule filter bar (editorial index feel) ─────────────────────────── */
.schedule-filters {
  display: flex;
  gap: 1.25rem;
  align-items: flex-end;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  padding: 0.65rem 0 0.85rem;
  border-bottom: var(--rule-thin) dashed var(--paper-edge);
}

.schedule-filters__field {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
}

.schedule-filters__field label {
  font-family: var(--font-ui);
  font-size: 0.62rem;
  font-weight: 600;
  color: var(--ink-faint);
  text-transform: uppercase;
  letter-spacing: 0.22em;
}

.schedule-filters__field input {
  padding: 0.45rem 0.65rem;
  border: var(--rule-thin) solid var(--ink);
  border-radius: 0;
  background: var(--paper);
  color: var(--ink);
  font-size: 0.85rem;
  font-family: var(--font-data);
}

.schedule-filters__field input:focus {
  outline: none;
  border-color: var(--ox);
  box-shadow: 0 0 0 2px var(--ox-10);
}

.schedule-filters__clear {
  padding: 0.45rem 1rem;
  border: var(--rule-thin) solid var(--ink);
  background: transparent;
  color: var(--ink);
  cursor: pointer;
  font-family: var(--font-ui);
  font-size: 0.64rem;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  transition:
    background 0.15s ease,
    color 0.15s ease;
}

.schedule-filters__clear:hover {
  background: var(--ink);
  color: var(--paper);
}

/* ─── Responsive refinements ─────────────────────────────────────────────── */
@media (max-width: 640px) {
  .tg-panel__header {
    align-items: flex-start;
  }

  .tg-panel__heading {
    font-size: 1.5rem;
  }

  .game-card__matchup {
    font-size: 1.3rem;
  }

  .game-card__header {
    flex-wrap: wrap;
  }

  .game-card__header-right {
    align-items: flex-start;
    flex-direction: row;
    flex-wrap: wrap;
  }

  .game-card__odds {
    gap: 0.6rem;
  }

  .odds-col__price {
    font-size: 1.1rem;
  }
}

@media (max-width: 360px) {
  .game-card {
    padding: 0.9rem 0.85rem 1rem;
  }

  .tg-day__label {
    font-size: 1.5rem;
  }
}
</style>
